import {Component, ElementRef, forwardRef, HostListener, Input, OnInit} from '@angular/core';
import {ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR} from '@angular/forms';
import {CommonModule} from '@angular/common';
import {
    CountryCode,
    getCountries,
    getCountryCallingCode,
    getExampleNumber,
    parsePhoneNumberFromString,
    PhoneNumber
} from 'libphonenumber-js';
import examples from 'libphonenumber-js/examples.mobile';
import {createLogger} from "../../shared/helpers/logger/logger";

const logger = createLogger('PhoneInputComponent');

interface PhoneNumberOutput {
    number: string;
    internationalNumber: string;
    nationalNumber: string;
    countryCode: string;
    dialCode: string;
}

type CountryItem = { type: 'country'; code: string; name: string; dialCode: string };
type SeparatorItem = { type: 'separator' };
type DropdownItem = CountryItem | SeparatorItem;

@Component({
    standalone: true,
    selector: 'app-phone-input',
    imports: [CommonModule, FormsModule],
    templateUrl: './phone-input.component.html',
    styleUrls: ['./phone-input.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => PhoneInputComponent),
            multi: true
        }
    ]
})
export class PhoneInputComponent implements OnInit, ControlValueAccessor {
    @Input() cssClass: string = '';
    @Input() preferredCountries: string[] = ['IN'];
    @Input() enableAutoCountrySelect: boolean = true;
    @Input() enablePlaceholder: boolean = true;
    @Input() customPlaceholder: string = '';
    @Input() numberFormat: 'International' | 'National' = 'International';
    @Input() separateDialCode: boolean = false;
    @Input() selectFirstCountry: boolean = false;
    @Input() phoneValidation: boolean = true;
    @Input() inputId: string = 'phone';
    @Input() dropdownPosition: 'auto' | 'top' | 'bottom' = 'auto';

    displayNumber: string = '';
    selectedCountry: string = 'IN';
    isDropdownOpen: boolean = false;
    searchTerm: string = '';
    filteredCountries: DropdownItem[] = [];
    focusedIndex: number = -1;
    errorMessage: string = '';
    isValid: boolean = false;
    isDropdownAbove: boolean = false;

    countries: CountryItem[] = [];

    private regionNames = new Intl.DisplayNames(['en'], {type: 'region'});

    constructor(private elementRef: ElementRef) {
    }

    ngOnInit() {
        this.countries = getCountries().map(code => ({
            type: 'country',
            code,
            name: this.getCountryName(code),
            dialCode: '+' + getCountryCallingCode(code)
        }));

        if (this.enableAutoCountrySelect) {
            this.selectedCountry = this.getInitialCountry();
        } else if (this.selectFirstCountry && this.preferredCountries.length > 0) {
            this.selectedCountry = this.preferredCountries[0];
        } else {
            this.selectedCountry = 'IN';
        }

        this.filterCountries();
    }

    getCountryName(code: string): string {
        try {
            return this.regionNames.of(code) || code;
        } catch (error) {
            console.warn(`Could not get name for country code: ${code}`, error);
            return code;
        }
    }

    getInitialCountry(): string {
        const language = navigator.language;
        const mapping: { [key: string]: string } = {
            'en-US': 'US',
            'en-GB': 'GB',
            'hi-IN': 'IN'
        };
        return mapping[language] || this.preferredCountries[0] || 'IN';
    }

    getSortedCountries(): CountryItem[] {
        const preferred = this.countries.filter(c => this.preferredCountries.includes(c.code));
        const others = this.countries.filter(c => !this.preferredCountries.includes(c.code));
        return [...preferred, ...others];
    }

    toggleDropdown() {
        this.isDropdownOpen = !this.isDropdownOpen;
        if (this.isDropdownOpen) {
            this.searchTerm = '';
            this.filterCountries();
            this.focusedIndex = this.filteredCountries.findIndex(item => item.type === 'country');
            setTimeout(() => {
                const searchInput = this.elementRef.nativeElement.querySelector('.search-input') as HTMLInputElement;
                if (searchInput) searchInput.focus();
                if (this.dropdownPosition === 'auto') {
                    this.adjustDropdownPosition();
                }
            }, 0);
        }
    }

    filterCountries() {
        const term = this.searchTerm.toLowerCase();
        const allCountries = this.getSortedCountries();
        const filteredPreferred = allCountries.filter(c => this.preferredCountries.includes(c.code) &&
            (c.name.toLowerCase().includes(term) || c.dialCode.includes(term) || c.code.toLowerCase().includes(term)));
        const filteredOthers = allCountries.filter(c => !this.preferredCountries.includes(c.code) &&
            (c.name.toLowerCase().includes(term) || c.dialCode.includes(term) || c.code.toLowerCase().includes(term)));
        const preferredItems: CountryItem[] = filteredPreferred;
        const otherItems: CountryItem[] = filteredOthers;
        this.filteredCountries = [...preferredItems];
        if (preferredItems.length > 0 && otherItems.length > 0) {
            this.filteredCountries.push({type: 'separator'});
        }
        this.filteredCountries.push(...otherItems);
        this.focusedIndex = -1;
    }

    selectCountry(country: CountryItem) {
        this.selectedCountry = country.code;
        this.isDropdownOpen = false;
        this.updateDisplayNumber();
        this.validatePhoneNumber();
    }

    onInputChange(event: Event) {
        const input = event.target as HTMLInputElement;
        this.displayNumber = input.value;
        this.detectCountryFromInput();
        this.validatePhoneNumber();
    }

    onBlur() {
        this.validatePhoneNumber();
    }

    getFlagUrl(countryCode: string): string {
        return `https://flagcdn.com/20x15/${countryCode.toLowerCase() === 'ac' || countryCode.toLowerCase() === 'ta' ? 'sh' : countryCode.toLowerCase()}.webp`;
    }

    updateDisplayNumber() {
        if (this.separateDialCode) {
            const dialCode = this.getDialCode(this.selectedCountry);
            if (!this.displayNumber.startsWith(dialCode)) {
                this.displayNumber = dialCode + ' ' + this.displayNumber;
            }
        }
    }

    detectCountryFromInput() {
        if (!this.displayNumber) return;
        try {
            const phoneNumber = parsePhoneNumberFromString(this.displayNumber);
            if (phoneNumber && phoneNumber.country) {
                this.selectedCountry = phoneNumber.country;
                this.updateDisplayNumber();
            }
        } catch (error) {
            logger.error('Error detecting country from input:', error);
        }
    }

    validatePhoneNumber() {
        if (!this.phoneValidation) {
            this.isValid = true;
            this.onChange(this.displayNumber);
            return;
        }

        let phoneNumber: PhoneNumber | undefined;
        try {
            phoneNumber = parsePhoneNumberFromString(this.displayNumber, this.selectedCountry as CountryCode);
        } catch (error) {
            logger.error('Error parsing phone number:', error);
        }

        if (phoneNumber && phoneNumber.isValid()) {
            this.errorMessage = '';
            this.isValid = true;
            const output: PhoneNumberOutput = {
                number: phoneNumber.number,
                internationalNumber: phoneNumber.formatInternational(),
                nationalNumber: phoneNumber.formatNational(),
                countryCode: phoneNumber.country || this.selectedCountry,
                dialCode: '+' + getCountryCallingCode(phoneNumber.country as CountryCode)
            };
            this.onChange(output);
        } else {
            this.errorMessage = 'Invalid phone number';
            this.isValid = false;
            this.onChange(null);
        }
    }

    getPlaceholder(): string {
        if (!this.enablePlaceholder) return '';
        if (this.customPlaceholder) return this.customPlaceholder;
        const example = getExampleNumber(this.selectedCountry as CountryCode, examples);
        if (example) {
            return this.separateDialCode ? example.formatNational() : example.formatInternational();
        }
        return 'Enter phone number';
    }

    getDialCode(countryCode: string): string {
        const country = this.countries.find(c => c.code === countryCode);
        return country ? country.dialCode : '';
    }

    writeValue(value: any): void {
        if (value) {
            if (typeof value === 'object' && value.countryCode && value.number) {
                this.selectedCountry = value.countryCode;
                const phoneNumber = parsePhoneNumberFromString(value.number, value.countryCode as CountryCode);
                if (phoneNumber) {
                    this.displayNumber = this.separateDialCode ? phoneNumber.formatNational() : phoneNumber.number;
                } else {
                    this.displayNumber = value.number;
                }
            } else if (typeof value === 'string') {
                const phoneNumber = parsePhoneNumberFromString(value);
                if (phoneNumber) {
                    this.selectedCountry = phoneNumber.country || this.selectedCountry;
                    this.displayNumber = this.separateDialCode ? phoneNumber.formatNational() : phoneNumber.number;
                } else {
                    this.displayNumber = value;
                }
            } else {
                this.displayNumber = '';
            }
        } else {
            this.displayNumber = '';
        }
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        // Implement if needed
    }

    onKeydown(event: KeyboardEvent) {
        if (event.key === 'ArrowDown') {
            const nextIndex = this.getNextCountryIndex(this.focusedIndex, 'next');
            if (nextIndex !== -1) this.focusedIndex = nextIndex;
            event.preventDefault();
        } else if (event.key === 'ArrowUp') {
            const prevIndex = this.getNextCountryIndex(this.focusedIndex, 'prev');
            if (prevIndex !== -1) this.focusedIndex = prevIndex;
            event.preventDefault();
        } else if (event.key === 'Enter' && this.focusedIndex >= 0) {
            const selectedItem = this.filteredCountries[this.focusedIndex];
            if (selectedItem.type === 'country') {
                this.selectCountry(selectedItem);
            }
            event.preventDefault();
        }
    }

    getNextCountryIndex(currentIndex: number, direction: 'next' | 'prev'): number {
        let index = currentIndex;
        do {
            index += direction === 'next' ? 1 : -1;
            if (index < 0 || index >= this.filteredCountries.length) {
                return -1;
            }
        } while (this.filteredCountries[index].type !== 'country');
        return index;
    }

    @HostListener('document:click', ['$event'])
    onClickOutside(event: Event) {
        const target = event.target as HTMLElement;
        if (!target.closest('.phone-input-wrapper') && this.isDropdownOpen) {
            this.isDropdownOpen = false;
        }
    }

    protected onTouched: () => void = () => {
    };

    private adjustDropdownPosition() {
        const wrapper = this.elementRef.nativeElement.querySelector('.phone-input-wrapper');
        const dropdown = this.elementRef.nativeElement.querySelector('.dropdown');
        if (!wrapper || !dropdown) return;

        const wrapperRect = wrapper.getBoundingClientRect();
        const dropdownHeight = dropdown.offsetHeight;
        const spaceBelow = window.innerHeight - wrapperRect.bottom;
        const spaceAbove = wrapperRect.top;

        // Default to below unless there's not enough space
        this.isDropdownAbove = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;
    }

    private onChange: (value: any) => void = () => {
    };
}
